import React, { Suspense, useEffect } from 'react';
import { Redirect, Route as ReactRouterRoute, Switch, useLocation, useRouteMatch } from 'react-router-dom';
import Loading from 'components/shared-components/Loading';
import { COMMUNITY_PREFIX_PATH } from 'configs/AppConfig';
import { useCurrentActiveMember, useCurrentMember, useMyMembers } from '../../redux/selector';
import { usePrefetchFolderDetail, usePrefetchFolderTree } from '../../hooks/Document';
import { DOCUMENTS_ROOT_PATH } from '../../utils/DocumentUtils';
import { usePrefetchMembers } from '../../hooks/Member';
import { usePrefetchProperties } from '../../hooks/Property';
import { lazyPreload } from './components/LazyPreload';
import { usePrefetchPostAggregates } from '../../hooks/Forum';
import { withSentryRouting } from '@sentry/react';
import { useCommunityPrefixPath } from '../../hooks/Routing';
import LoadCurrentCommunity from './LoadCurrentCommunity';
import AppLayout from '../../layouts/app-layout';
import CommunitySwitcher from './CommunitySwitcher';
import { MemberStatus } from '@quick/client';
import utils from 'utils';
import { NavItem } from '../../layouts/app-layout/NavItem';
import Icon from './components/Icon';
import MenuContent from '../../components/layout-components/MenuContent';
import AppBreadcrumb from '../../components/layout-components/AppBreadcrumb';
import NavProfile from './NavProfile';
import { isCommunityAdmin } from '../../utils/MemberUtils';
import { useCurrentCommunity } from '../../hooks/Community';
import SensitiveRoute from './SensitiveRoute';
import { useIdentifyCurrentUserAndMemberIfNeeded } from '../../integration';
import {
  useFeatureAccountingEnabled,
  useFeatureCommunityEnabled,
  useFeatureFinancialInsightEnabled,
} from '../../hooks/FeatureFlags';

const Route = withSentryRouting(ReactRouterRoute);

// Community.
const LazyOnboarding = lazyPreload(() => import('./onboarding'));
const LazyDiscussions = lazyPreload(() => import('./forum'));
const LazyProperties = lazyPreload(() => import('./properties'));
const LazyMembers = lazyPreload(() => import('./members'));
const LazyRequests = lazyPreload(() => import('./requests'));
export const LazyNewRequest = lazyPreload(() => import('./requests/NewRequest'));
export const LazyRequestDetail = lazyPreload(() => import('./requests/RequestDetail'));
const LazyDocuments = lazyPreload(() => import('./documents'));
const LazyCalendar = lazyPreload(() => import('./calendars'));
export const LazyProfile = lazyPreload(() => import('./profile'));
const LazyAccountSecurity = lazyPreload(() => import('./account-security'));
export const LazyNotifications = lazyPreload(() => import('./notification/Notifications'));
export const LazyMemberProfile = lazyPreload(() => import('./profile/MemberProfile'));
// My Unit.
export const LazyMyUnitAccountBills = lazyPreload(() => import('./my-unit/bills'));
export const LazyMyUnitRecurringBills = lazyPreload(() => import('./my-unit/recurring-bills'));
// Treasury.
export const LazyTreasuryAccountTransactions = lazyPreload(() => import('./treasury/transactions'));
export const LazyTreasuryCards = lazyPreload(() => import('./treasury/cards'));
export const LazyTreasuryAccountInvoices = lazyPreload(() => import('./treasury/invoices'));
export const LazyTreasuryAccountRecurringInvoices = lazyPreload(() => import('./treasury/recurring-invoices'));
export const LazyTreasuryAccountBills = lazyPreload(() => import('./treasury/bills'));
export const LazyTreasuryAccountBillApprovals = lazyPreload(() => import('./treasury/bill-approvals'));
export const LazyTreasuryAccountVendors = lazyPreload(() => import('./treasury/vendors'));
export const LazyTreasuryAccountNewVendor = lazyPreload(() => import('./treasury/vendors/new-vendor'));
export const LazyTreasuryAccountVendorDetail = lazyPreload(() => import('./treasury/vendors/vendor-detail'));
export const LazyTreasuryReports = lazyPreload(() => import('./treasury/reports'));
export const LazyTreasuryAccounting = lazyPreload(() => import('./treasury/accounting'));
export const LazyTreasuryInsights = lazyPreload(() => import('./treasury/insights'));
export const LazyTreasuryBudgets = lazyPreload(() => import('./treasury/budgets'));

const preLoadPages = async () => {
  await LazyDiscussions.preload();
  await LazyProperties.preload();
  await LazyMembers.preload();
  await LazyRequests.preload();
  await LazyDocuments.preload();
  await LazyCalendar.preload();
};

const MemberRoutes = () => {
  usePrefetchPostAggregates();
  usePrefetchFolderDetail(DOCUMENTS_ROOT_PATH);
  usePrefetchFolderTree();
  usePrefetchProperties();
  usePrefetchMembers();

  useEffect(() => {
    preLoadPages();
  }, []);
  const { url } = useRouteMatch();
  const appPrefixPath = useCommunityPrefixPath();
  const currentActiveMember = useCurrentActiveMember();
  return (
    <LoadCurrentCommunity>
      <Switch>
        <Route path={`${url}/join`} component={LazyOnboarding} />
        {/* Redirects to tryforest.com/app/join or tryforest.com/app/1/join if the member becomes inactive. */}
        {!currentActiveMember && <Redirect to={`${appPrefixPath}/join`} />}
        <Route path={`${url}/forum`} component={LazyDiscussions} />
        <Route path={`${url}/properties`} component={LazyProperties} />
        <Route path={`${url}/members/:memberId`} component={LazyMemberProfile} />
        <Route path={`${url}/members`} component={LazyMembers} />
        <Route path={`${url}/profile`} component={LazyProfile} />
        <Route path={`${url}/account-security`} component={LazyAccountSecurity} />
        <Route path={`${url}/notifications`} component={LazyNotifications} />
        <Route path={`${url}/requests/new`} component={LazyNewRequest} />
        <Route path={`${url}/requests/:requestId`} component={LazyRequestDetail} />
        <Route path={`${url}/requests`} component={LazyRequests} />
        <Route path={`${url}/documents`} component={LazyDocuments} />
        <Route path={`${url}/documents/:path`} component={LazyDocuments} />
        <Route path={`${url}/calendar`} component={LazyCalendar} />
        {/* My unit. */}
        <Route path={`${url}/myUnit/payments/autopay`} component={LazyMyUnitRecurringBills} />
        <Route path={`${url}/myUnit/payments`} component={LazyMyUnitAccountBills} />
        {/* Treasury. */}
        <SensitiveRoute path={`${url}/treasury/transactions`}>
          <LazyTreasuryAccountTransactions />
        </SensitiveRoute>
        <SensitiveRoute path={`${url}/treasury/cards`}>
          <LazyTreasuryCards />
        </SensitiveRoute>
        <SensitiveRoute path={`${url}/treasury/invoices/recurringInvoices`}>
          <LazyTreasuryAccountRecurringInvoices />
        </SensitiveRoute>
        <SensitiveRoute path={`${url}/treasury/invoices`}>
          <LazyTreasuryAccountInvoices />
        </SensitiveRoute>
        <SensitiveRoute path={`${url}/treasury/payBills/approvalPolicies`}>
          <LazyTreasuryAccountBillApprovals />
        </SensitiveRoute>
        <SensitiveRoute path={`${url}/treasury/payBills`}>
          <LazyTreasuryAccountBills />
        </SensitiveRoute>
        <SensitiveRoute exact path={`${url}/treasury/vendors/:vendorRelationId`}>
          <LazyTreasuryAccountVendorDetail />
        </SensitiveRoute>
        <SensitiveRoute path={`${url}/treasury/vendors`}>
          <LazyTreasuryAccountVendors />
        </SensitiveRoute>
        <SensitiveRoute path={`${url}/treasury/newVendor`}>
          <LazyTreasuryAccountNewVendor />
        </SensitiveRoute>
        <SensitiveRoute path={`${url}/treasury/insights`}>
          <LazyTreasuryInsights />
        </SensitiveRoute>
        <SensitiveRoute path={`${url}/treasury/budgets`}>
          <LazyTreasuryBudgets />
        </SensitiveRoute>
        <SensitiveRoute path={`${url}/treasury/accounting`}>
          <LazyTreasuryAccounting />
        </SensitiveRoute>
        <SensitiveRoute path={`${url}/treasury/reports`}>
          <LazyTreasuryReports />
        </SensitiveRoute>
        <Redirect to={`${appPrefixPath}/forum`} />
      </Switch>
    </LoadCurrentCommunity>
  );
};

export const AppViews = () => {
  useIdentifyCurrentUserAndMemberIfNeeded();
  const members = useMyMembers();
  return (
    <Suspense fallback={<Loading cover="content" />}>
      <Switch>
        {/* If the user has any members, navigate from tryforest.com/app/join to tryforest.com/app/:communityId */}
        {members.length === 0 && <Route path={`${COMMUNITY_PREFIX_PATH}/join`} component={LazyOnboarding} />}
        <Route path={`${COMMUNITY_PREFIX_PATH}/:communityId`} component={MemberRoutes} />
        <Route path={COMMUNITY_PREFIX_PATH} component={MemberRoutes} />
      </Switch>
    </Suspense>
  );
};

const CommunityLayout = () => {
  const location = useLocation();
  const navItems = useCommunityNavigationConfig();
  const currentRouteInfo = utils.getRouteInfo(navItems, location.pathname) as NavItem;
  const currentMember = useCurrentMember();
  return (
    <AppLayout
      navMenuContent={
        <MenuContent
          routeInfo={currentRouteInfo}
          switcher={<CommunitySwitcher />}
          navItems={navItems}
          isNavEnabled={currentMember?.status === MemberStatus.Active}
        />
      }
      navProfile={<NavProfile />}
      pageHeader={<AppBreadcrumb routeInfo={currentRouteInfo} navItems={navItems} />}
      appView={<AppViews />}
    />
  );
};

const useCommunityNavigationConfig = (): NavItem[] => {
  const myCommunityNavConfig = useMyCommunityNavigationConfig();
  const myUnitNavConfig = useMyUnitNavigationConfig();
  const treasuryNavConfig = useTreasuryNavigationConfig();
  const accountNavConfig = useAccountingNavigationConfig();
  return [...myCommunityNavConfig, ...myUnitNavConfig, ...treasuryNavConfig, ...accountNavConfig];
};

const useMyCommunityNavigationConfig = (): NavItem[] => {
  const appPrefixPath = useCommunityPrefixPath();
  const adminOnlyConfig = useMyCommunityAdminOnlyNavigationConfig();
  const featureCommunityEnabled = useFeatureCommunityEnabled();
  if (!featureCommunityEnabled) {
    return [];
  }
  return [
    {
      key: 'community',
      path: `${appPrefixPath}/forum`,
      title: 'Community',
      breadcrumb: false,
      submenu: [
        {
          key: 'forum',
          path: `${appPrefixPath}/forum`,
          title: 'Forum',
          icon: ({ isActive }) => (isActive ? <Icon name="people-fill" /> : <Icon name="people" />),
          breadcrumb: false,
          submenu: [],
        },
        {
          key: 'Resident Requests',
          path: `${appPrefixPath}/requests`,
          title: 'Requests',
          icon: ({ isActive }) => (isActive ? <Icon name="request-fill" /> : <Icon name="request" />),
          breadcrumb: false,
          submenu: [],
        },
        {
          key: 'calendar',
          path: `${appPrefixPath}/calendar`,
          title: 'Calendar',
          icon: ({ isActive }) => (isActive ? <Icon name="calendar" /> : <Icon name="calendar" />),
          breadcrumb: true,
          submenu: [],
        },
        {
          key: 'documents',
          path: `${appPrefixPath}/documents`,
          title: 'Documents',
          icon: ({ isActive }) => (isActive ? <Icon name="folder-open-fill" /> : <Icon name="folder-open" />),
          breadcrumb: false,
          submenu: [],
        },
        {
          key: 'properties',
          path: `${appPrefixPath}/properties`,
          title: 'Properties',
          icon: ({ isActive }) => (isActive ? <Icon name="home-fill" /> : <Icon name="home" />),
          breadcrumb: true,
          submenu: [],
        },
        ...adminOnlyConfig,
      ],
    },
  ];
};

const useMyCommunityAdminOnlyNavigationConfig = (): NavItem[] => {
  const appPrefixPath = useCommunityPrefixPath();
  const currentActiveMember = useCurrentActiveMember();
  if (!currentActiveMember || !isCommunityAdmin(currentActiveMember)) {
    return [];
  }
  return [
    {
      key: 'members',
      path: `${appPrefixPath}/members`,
      title: 'Members',
      icon: ({ isActive }) => (isActive ? <Icon name="journal-medical-fill" /> : <Icon name="journal-medical" />),
      breadcrumb: true,
      submenu: [],
    },
  ];
};

const useMyUnitNavigationConfig = (): NavItem[] => {
  const appPrefixPath = useCommunityPrefixPath();
  const currentCommunity = useCurrentCommunity();
  const currentActiveMember = useCurrentActiveMember();
  if (
    !currentActiveMember ||
    !currentActiveMember.property ||
    !currentCommunity ||
    !currentCommunity.config.vendorlinkEnabled ||
    (!currentCommunity.vendorlinkAccountId && !isCommunityAdmin(currentActiveMember))
  ) {
    return [];
  }
  return [
    {
      key: 'myUnit',
      path: `${appPrefixPath}/myUnit/payments`,
      title: 'My Unit',
      breadcrumb: true,
      submenu: [
        {
          key: 'vendorLinkPayBills',
          path: `${appPrefixPath}/myUnit/payments`,
          title: 'Payments',
          icon: ({ isActive }) => <Icon name="pay-fill" />,
          breadcrumb: true,
          onMouseEnter: () => {
            LazyMyUnitAccountBills.preload();
          },
          submenu: [],
        },
      ],
    },
  ];
};

const useTreasuryNavigationConfig = (): NavItem[] => {
  const appPrefixPath = useCommunityPrefixPath();
  const currentCommunity = useCurrentCommunity();
  const currentActiveMember = useCurrentActiveMember();
  if (
    !currentActiveMember ||
    !isCommunityAdmin(currentActiveMember) ||
    !currentCommunity ||
    !currentCommunity.config.vendorlinkEnabled
  ) {
    return [];
  }
  let cardNavItems: NavItem[] = [];
  if (currentCommunity.config.cardEnabled) {
    cardNavItems = [
      {
        key: 'vendorLinkCards',
        path: `${appPrefixPath}/treasury/cards`,
        title: 'Cards',
        icon: ({ isActive }) => <Icon name="card" />,
        breadcrumb: false,
        submenu: [],
        onMouseEnter: () => {
          LazyTreasuryCards.preload();
        },
      },
    ];
  }
  return [
    {
      key: 'treasury',
      path: `${appPrefixPath}/treasury/payBills`,
      title: 'Treasury',
      breadcrumb: true,
      submenu: [
        {
          key: 'vendorLinkTransactions',
          path: `${appPrefixPath}/treasury/transactions`,
          title: 'Banking',
          icon: ({ isActive }) => <Icon name="arrow-left-right" />,
          breadcrumb: true,
          submenu: [],
          onMouseEnter: () => {
            LazyTreasuryAccountTransactions.preload();
          },
        },
        ...cardNavItems,
        {
          key: 'vendorLinkPayBills',
          path: `${appPrefixPath}/treasury/payBills`,
          title: 'Pay bills',
          icon: ({ isActive }) => <Icon name="pay-fill" />,
          breadcrumb: true,
          submenu: [],
          onMouseEnter: () => {
            LazyTreasuryAccountBills.preload();
          },
        },
        {
          key: 'vendorLinkInvoices',
          path: `${appPrefixPath}/treasury/invoices`,
          title: 'Assessments',
          icon: ({ isActive }) => <Icon name="get-paid-fill" />,
          breadcrumb: true,
          submenu: [],
          onMouseEnter: () => {
            LazyTreasuryAccountInvoices.preload();
          },
        },
        {
          key: 'vendorLinkContacts',
          path: `${appPrefixPath}/treasury/vendors`,
          title: 'Vendors',
          icon: ({ isActive }) => <Icon name="journal-medical-fill-inverted" />,
          breadcrumb: true,
          submenu: [],
          onMouseEnter: () => {
            LazyTreasuryAccountVendors.preload();
          },
        },
      ],
    },
  ];
};

const useAccountingNavigationConfig = (): NavItem[] => {
  const appPrefixPath = useCommunityPrefixPath();
  const currentCommunity = useCurrentCommunity();
  const currentActiveMember = useCurrentActiveMember();
  const featureAccountingEnabled = useFeatureAccountingEnabled();
  const financialInsightEnabled = useFeatureFinancialInsightEnabled();
  if (
    !currentActiveMember ||
    !isCommunityAdmin(currentActiveMember) ||
    !currentCommunity ||
    !currentCommunity.config.vendorlinkEnabled
  ) {
    return [];
  }
  if (!currentCommunity.config.accountingEnabled && !featureAccountingEnabled) {
    return [];
  }
  const insight: NavItem = {
    key: 'vendorLinkOverview',
    path: `${appPrefixPath}/treasury/insights`,
    title: 'Insights',
    icon: ({ isActive }) => <Icon name="insight" />,
    breadcrumb: false,
    submenu: [],
    onMouseEnter: () => {
      LazyTreasuryInsights.preload();
    },
  };
  const budget: NavItem = {
    key: 'vendorLinkBudget',
    path: `${appPrefixPath}/treasury/budgets`,
    title: 'Budgets',
    icon: ({ isActive }) => <Icon name="budget" />,
    breadcrumb: false,
    submenu: [],
    onMouseEnter: () => {
      LazyTreasuryBudgets.preload();
    },
  };
  const bookkeeping: NavItem = {
    key: 'vendorLinkAccounting',
    path: `${appPrefixPath}/treasury/accounting`,
    title: 'Bookkeeping',
    icon: ({ isActive }) => <Icon name="bookkeeping" />,
    breadcrumb: false,
    submenu: [],
    onMouseEnter: () => {
      LazyTreasuryAccounting.preload();
    },
  };
  const reports: NavItem = {
    key: 'vendorLinkReports',
    path: `${appPrefixPath}/treasury/reports`,
    title: 'Reports',
    icon: ({ isActive }) => <Icon name="report" />,
    breadcrumb: false,
    submenu: [],
    onMouseEnter: () => {
      LazyTreasuryReports.preload();
    },
  };
  return [
    {
      key: 'accounting',
      path: `${appPrefixPath}/treasury/accounting`,
      title: 'Financials',
      breadcrumb: false,
      submenu: financialInsightEnabled ? [insight, budget, reports, bookkeeping] : [bookkeeping, reports],
    },
  ];
};

export default React.memo(CommunityLayout);
